import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { reviewDishName } from '../utils/reviewGrouping';
import DishPhoto from './DishPhoto';

// #116/#110: the ONE read-only card a multi-dish public visit collapses into —
// shared visit text/date/author once, then each rated dish as a row with its own
// rating + photos. Extracted so the restaurant/food Reviews list (ReviewList) and
// the public profile (UserProfilePage) render it identically and can't drift again.
// Report affordances are opt-in: pass onReportReview/onReportPhoto (+ token +
// currentUser) where a signed-in viewer can flag content; omit them for a plain
// read-only surface.
function VisitReviewGroupCard({ group, onReportReview, onReportPhoto, currentUser, token }) {
  const navigate = useNavigate();
  const head = group[0];

  const renderStars = (rating) => (
    <div className="star-display">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} style={{ color: star <= rating ? '#ffc107' : '#e4e5e9', fontSize: '1.2rem' }}>★</span>
      ))}
    </div>
  );

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // A signed-in viewer who isn't the author can flag someone else's content.
  const canReport = (review) =>
    onReportReview && token && currentUser && review.user_id !== currentUser.id;

  return (
    <Card className="mb-3">
      <Card.Body>
        {/* Visit header — the container. Reads "Visit rated by X on DATE", with the
            experience rating below; the dishes fold in under it. */}
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <Badge bg="light" text="dark" className="border">🍽️ Visit</Badge>
              <span className="text-muted small">
                rated by{' '}
                <span style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }} onClick={() => navigate(`/profile/${head.user_id}`)}>
                  {/* #171: no email fallback — see ReviewList. */}
                  {head.user_display_name || 'Anonymous'}
                </span>
                {' '}on {formatDate(head.visit_date || head.created_at)}
              </span>
              {head.user_is_verified && (<Badge bg="success">Verified</Badge>)}
            </div>
            {/* #110: the visit's overall experience rating (service/ambience/value),
                distinct from the per-dish ratings. Only when set. */}
            {head.visit_experience_rating > 0 && (
              <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                {renderStars(head.visit_experience_rating)}
                <span className="text-muted small">Overall experience</span>
              </div>
            )}
          </div>
          {canReport(head) && (
            <Button variant="link" size="sm" className="text-muted p-0" title="Report this review" onClick={() => onReportReview(head)}>
              ⚑ Report
            </Button>
          )}
        </div>

        {head.review_text && (
          <p className="mt-2" style={{ whiteSpace: 'pre-line' }}>{head.review_text}</p>
        )}

        {/* Dishes folded INTO this visit — a tinted, labelled sub-panel so they read
            as part of the visit, not as separate review entries. */}
        <div className="mt-3 bg-light rounded border">
          <div className="px-3 pt-2 pb-1 text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
            {group.length} {group.length === 1 ? 'dish' : 'dishes'} on this visit
          </div>
          {group.map((r, i) => (
            <div key={r.id} className={`px-3 py-2 ${i > 0 ? 'border-top' : ''}`}>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="fw-semibold">{reviewDishName(r) || 'Dish'}</span>
                {renderStars(r.rating)}
              </div>
              {/* #198: the same photo can appear against several dishes on this
                  visit — one table shot, one upload. Framed to THIS dish by its
                  own crop, so the coffee's row shows the coffee. */}
              {r.photos && r.photos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {r.photos.map((photo) => (
                    <DishPhoto
                      key={photo.id}
                      photo={photo}
                      visitDishId={r.visit_dish_id}
                      alt={reviewDishName(r) || 'Visit'}
                    >
                      {onReportPhoto && canReport(r) && (
                        <Button variant="link" size="sm" className="text-muted p-0 position-absolute top-0 end-0" title="Report this photo" style={{ lineHeight: 1, background: 'rgba(255,255,255,0.85)', borderRadius: '4px' }} onClick={(e) => { e.stopPropagation(); onReportPhoto(photo.id); }}>
                          ⚑
                        </Button>
                      )}
                    </DishPhoto>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {(head.meal_type || head.location_name || head.location_city || head.location_address) && (
          <div className="mt-3 text-muted small d-flex flex-wrap gap-3">
            {head.meal_type && (
              <span>🍽️ {head.meal_type.charAt(0).toUpperCase() + head.meal_type.slice(1)}</span>
            )}
            {(head.location_name || head.location_city || head.location_address) && (
              <span>📍 {[
                head.location_name || head.location_address,
                [head.location_city, head.location_state].filter(Boolean).join(', ')
              ].filter(Boolean).join(' — ')}</span>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default VisitReviewGroupCard;
